import { ref, onMounted, onBeforeUnmount, nextTick, type Ref, type ComponentPublicInstance } from "vue";

type Options = {
    bodyHeightOffset?: number;
    bodyWidthOffset?: number;
    bodyRef: Ref<ComponentPublicInstance | HTMLElement | null>;
};

function delay(ms: number) {
    return new Promise(res => setTimeout(res, ms));
}

function isVueComponent(value: unknown) {
    return !!(value as ComponentPublicInstance)?.$el;
}

export function useScalingBannerWrapper(options: Options) {
    // 0 - free scaling, 1 - 100% scaling
    const scalingFactor = ref(0);
    const bodyHeightOffset = ref(options.bodyHeightOffset ?? 0);
    const bodyWidthOffset = ref(options.bodyWidthOffset ?? -50);

    const bodyDimensionsPixels = ref({
        height: 0,
        width: 0
    });

    function setBodyDimensionsPixels() {
        const rawValue = options.bodyRef?.value;

        if (!rawValue) return;

        let el: HTMLElement | null = null;

        if (rawValue && isVueComponent(rawValue)) {
            el = (rawValue as ComponentPublicInstance).$el as HTMLElement;
        } else {
            el = rawValue as HTMLElement;
        }

        if (!el) return;

        const bodyRect = el.getBoundingClientRect();

        if (
            bodyDimensionsPixels.value.height !== bodyRect.height ||
            bodyDimensionsPixels.value.width !== bodyRect.width
        ) {
            bodyDimensionsPixels.value = {
                height: bodyRect.height + bodyHeightOffset.value,
                width: bodyRect.width + bodyWidthOffset.value
            };
        }

        if (bodyRect.height === 0 && bodyRect.width === 0) {
            setTimeout(setBodyDimensionsPixels, 100);
        }
    }

    async function waitAndRecalculate() {
        await delay(2000);
        setBodyDimensionsPixels();
    }

    onMounted(async () => {
        window.addEventListener("resize", setBodyDimensionsPixels, true);

        await nextTick(async () => {
            setBodyDimensionsPixels();
            await waitAndRecalculate();
        });
    });

    onBeforeUnmount(() => {
        window.removeEventListener("resize", setBodyDimensionsPixels, true);
    });

    return {
        scalingFactor,
        bodyHeightOffset,
        bodyWidthOffset,
        bodyDimensionsPixels,
        setBodyDimensionsPixels
    };
}
